import React, { useState, useEffect } from 'react';
import { ClipLoader } from 'react-spinners';
import { Plus } from 'lucide-react';
import PersonalInfoEditor from './PersonalInfoEditor';
import SummaryEditor from './SummaryEditor';
import SkillsEditor from './SkillsEditor';
import ExperienceEditor from './ExperienceEditor';
import ProjectsEditor from './ProjectsEditor';
import EducationEditor from './EducationEditor';
import DraggableSection from './DraggableSection';

import { getResumeJsonData, updateResumeSection, saveSectionOrder } from '../../api/resume';

const DEFAULT_SECTION_ORDER = ['summary', 'skills', 'experience', 'projects', 'education'];

const DEFAULT_SECTION_STRUCTURES = {
  summary: '',
  skills: [],
  experience: [],
  projects: [],
  education: [],
};

const SECTION_COMPONENTS = {
  summary: { Component: SummaryEditor, label: 'Summary' },
  skills: { Component: SkillsEditor, label: 'Skills' },
  experience: { Component: ExperienceEditor, label: 'Experience' },
  projects: { Component: ProjectsEditor, label: 'Projects' },
  education: { Component: EducationEditor, label: 'Education' },
};


const ResumeEditor = ({ resumeId = 'default' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getResumeJsonData(resumeId);
        if (result && result.success) {
          if (result.resp.sectionOrder && Array.isArray(result.resp.sectionOrder)) {
            const sectionOrder = result.resp.sectionOrder.map( section => section.toLowerCase() === 'work_experience' ? 'experience' : section.toLowerCase())
            setSectionOrder(sectionOrder);
          }
          setData(result.resp);
        } else {
          setError(result?.resp || 'Failed to load resume data. Please try again later.');
        }
      } catch (err) {
        setError('Failed to load resume data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [resumeId]);

  const SECTION_MAP = {
    personalInfo: 'PERSONAL_INFO',
    summary: 'SUMMARY',
    skills: 'SKILLS',
    experience: 'EXPERIENCE',
    projects: 'PROJECTS',
    education: 'EDUCATION'
  };

  const handleSaveSection = async (sectionName, sectionData) => {
    // Optimistic UI update
    setData((prev) => ({
      ...prev,
      [sectionName]: sectionData
    }));
    
    // API call point
    const sectionType = SECTION_MAP[sectionName];
    if (sectionType) {
      const result = await updateResumeSection(resumeId, sectionType, sectionData);
      if (!result?.success) {
        console.error(`Failed to save section ${sectionName}:`, result?.resp);
      } else {
        console.log(`Saved section ${sectionName}:`, sectionData);
      }
    }
  };

  const handleReorder = async (fromIndex, toIndex) => {
    const updated = [...sectionOrder];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setSectionOrder(updated);

    // Persist to backend
    const result = await saveSectionOrder(resumeId, updated);
    if (!result?.success) {
      console.error('Failed to save section order:', result?.resp);
    }
  };

  const handleAddSection = async (sectionKey) => {
    if (sectionOrder.includes(sectionKey)) return;

    const updatedOrder = [...sectionOrder, sectionKey];
    setSectionOrder(updatedOrder);

    // Initialize missing data structure if needed
    if (data && (data[sectionKey] === undefined || data[sectionKey] === null)) {
      const defaultVal = DEFAULT_SECTION_STRUCTURES[sectionKey] ?? [];
      setData((prev) => ({
        ...prev,
        [sectionKey]: defaultVal
      }));
    }

    // Persist updated order to backend
    const result = await saveSectionOrder(resumeId, updatedOrder);
    if (!result?.success) {
      console.error('Failed to save section order:', result?.resp);
    }
  };

  const handleRemoveSection = async (sectionKey) => {
    const updatedOrder = sectionOrder.filter((s) => s !== sectionKey);
    setSectionOrder(updatedOrder);

    // Persist updated order to backend
    const result = await saveSectionOrder(resumeId, updatedOrder);
    if (!result?.success) {
      console.error('Failed to save section order:', result?.resp);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-600">
        <ClipLoader color="#4f46e5" size={40} />
        <p className="mt-4 font-medium">Loading resume editor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-red-600">
        <div className="bg-red-50 p-6 rounded-lg border border-red-200 text-center max-w-md">
           <h3 className="text-lg font-bold mb-2">Error Loading Resume</h3>
           <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const availableSections = DEFAULT_SECTION_ORDER.filter(s => !sectionOrder.includes(s));

  return (
    <div className="bg-gray-100 min-h-screen py-10 px-2 sm:px-6">
      <div className="max-w-4xl mx-auto bg-white shadow-2xl min-h-[1056px] border border-gray-200 sm:px-16 px-4 py-12 print:shadow-none print:border-none print:m-0 print:p-0">
        
        {/* Global CSS for list styling in quill output to ensure standard resume look */}
        <style>{`
          .style-quill-lists ul, .style-quill-lists ol {
            list-style-type: disc !important;
            margin-top: 0.25rem !important;
            margin-bottom: 0.25rem !important;
          }
          .style-quill-lists ul li, .style-quill-lists ol li {
            list-style-type: disc !important;
          }
          .style-quill-lists li {
            margin-bottom: 0.25rem;
          }
          .resume-content strong {
            font-weight: 700;
          }
        `}</style>

        {/* Header Section */}
        <PersonalInfoEditor 
          data={data.personalInfo} 
          onSave={(newData) => handleSaveSection('personalInfo', newData)} 
        />
        
        {/* Main Content Sections — order controlled by drag-and-drop */}
        {sectionOrder.map((key, index) => {
          const config = SECTION_COMPONENTS[key];
          if (!config) return null;
          const { Component } = config;
          return (
            <DraggableSection key={key} id={key} index={index} onReorder={handleReorder}>
              <Component
                data={data[key]}
                onSave={(newData) => handleSaveSection(key, newData)}
                onRemove={() => handleRemoveSection(key)}
              />
            </DraggableSection>
          );
        })}

        {/* Add Section Controls */}
        {availableSections.length > 0 && (
          <div className="mt-8 pt-6 border-t border-dashed border-gray-300 print:hidden">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-600 font-sans flex items-center gap-1">
                <Plus size={16} /> Add Section:
              </span>
              {availableSections.map((key) => {
                const config = SECTION_COMPONENTS[key];
                return (
                  <button
                    key={key}
                    onClick={() => handleAddSection(key)}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-md border border-indigo-200 transition-colors flex items-center gap-1 shadow-sm font-sans"
                  >
                    <Plus size={14} /> {config?.label || key}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ResumeEditor;


